import React from "react";
import { Duration as LuxonDuration } from "luxon";
import { Duration as DurationType } from "luxon";
import { injectIntl } from "react-intl";
import { WrappedComponentProps } from "react-intl";
import { AsyncAction } from "../../../types/Action";
import RawFormattedMessage from "../RawFormattedMessage";

export type DurationProps = {
  alternativeCountdownLabel?: string;
  className?: string;
  description?: string | React.ReactNode;
  format: string;
  id?: string;
  label?: string | React.ReactNode;
  onChange?: (value: number) => any;
  onCountdownEnd?: (id: string) => AsyncAction<any>;
  showCountdown?: boolean;
  valueInSeconds: number;
};
export type DurationPropsWithIntl = WrappedComponentProps & DurationProps;

type DurationState = {
  duration: DurationType;
  tickId: NodeJS.Timeout | null;
  endId: NodeJS.Timeout | null;
};

export class Duration extends React.Component<
  DurationPropsWithIntl,
  DurationState
> {
  constructor(props: DurationPropsWithIntl) {
    super(props);
    const {
      intl: { locale },
      valueInSeconds,
    } = props;
    this.state = {
      duration: this.getDurationObject(locale, valueInSeconds),
      tickId: null,
      endId: null,
    };
  }

  getDurationObject(locale: string, seconds?: number): DurationType {
    return seconds
      ? LuxonDuration.fromObject({
          seconds,
          locale,
        })
      : LuxonDuration.invalid("No seconds passed to the constructor");
  }

  componentWillMount() {
    this.setupTimers();
  }

  componentWillUnmount() {
    this.tearDownTimers();
  }

  setupTimers(): false | void {
    const { id, onCountdownEnd } = this.props;
    if (!id || !onCountdownEnd) {
      return false;
    }
    const endId = global.setTimeout(() => {
      this.tearDownTimers();
      onCountdownEnd(id);
    }, this.state.duration.as("milliseconds"));
    const tickId = global.setInterval(() => {
      this.setState({
        duration: this.state.duration.minus(1000),
      });
    }, 1000);

    this.setState({
      tickId,
      endId,
    });
  }

  tearDownTimers() {
    if (this.state.tickId) {
      global.clearInterval(this.state.tickId);
    }
    if (this.state.endId) {
      global.clearTimeout(this.state.endId);
    }
    this.setState({
      tickId: null,
      endId: null,
    });
  }

  getMessageId() {
    if (
      this.props.onCountdownEnd &&
      !this.props.showCountdown &&
      this.props.alternativeCountdownLabel
    ) {
      return this.props.alternativeCountdownLabel;
    }
    return this.props.format
      ? `duration.${this.props.format}`
      : "duration.long";
  }

  render() {
    if (!this.state.duration.isValid) {
      return null;
    }
    const messageId = this.getMessageId();
    const duration = this.state.duration.shiftTo("hours", "minutes", "seconds");
    return (
      <div className={this.props.className}>
        <RawFormattedMessage
          values={{
            hours: duration.hours,
            minutes: duration.minutes,
            seconds: duration.seconds,
          }}
          id={messageId}
        />
      </div>
    );
  }
}

export default injectIntl(Duration);
